import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, BookOpen, Eye, EyeOff } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import { validateFormField, isHoneypotFilled, HONEYPOT_FIELD, checkRateLimit, markFormLoaded, isSubmitTooFast, sanitizeText } from '../utils/formValidation';

export default function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', password: '', [HONEYPOT_FIELD]: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { markFormLoaded('register'); }, []);

  const validate = () => {
    const e = {};
    const nameCheck = validateFormField(form.name, 'name');
    if (!nameCheck.valid) e.name = nameCheck.error;
    const emailCheck = validateFormField(form.email, 'email');
    if (!emailCheck.valid) e.email = emailCheck.error;
    if (form.password.length < 6) e.password = 'Password must be at least 6 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotFilled(form[HONEYPOT_FIELD])) return;
    if (isSubmitTooFast('register')) return;
    if (!checkRateLimit('register', 5000)) { setErrors({ form: 'Please wait a moment.' }); return; }
    if (!validate()) return;
    setLoading(true);
    setErrors({});
    try {
      const { data } = await api.post('/auth/register', { name: sanitizeText(form.name), email: sanitizeText(form.email), password: form.password });
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/ghostwriter/dashboard');
    } catch (e) {
      setErrors({ form: e.response?.data?.detail || 'Registration failed' });
    } finally { setLoading(false); }
  };

  return (
    <>
      <SeoHead title="Register — AI Ghostwriter Pro" noindex={true} />
      <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 md:mb-10">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <BookOpen className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="font-serif text-xl text-foreground">AI Ghostwriter <span className="text-primary">Pro</span></span>
            </Link>
            <h1 className="font-serif text-2xl sm:text-3xl tracking-tight mb-2">Create Account</h1>
            <p className="text-sm text-muted-foreground">Start your free trial — no credit card required</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5" data-testid="register-form" noValidate>
            {errors.form && <p className="text-sm text-red-400 text-center" data-testid="register-error">{errors.form}</p>}
            <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
              <input type="text" name={HONEYPOT_FIELD} value={form[HONEYPOT_FIELD]} onChange={(e) => setForm({...form, [HONEYPOT_FIELD]: e.target.value})} autoComplete="off" tabIndex={-1} />
            </div>
            <div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="text" required value={form.name} onChange={(e) => { setForm({...form, name: e.target.value}); if (errors.name) setErrors({...errors, name: null}); }}
                  data-testid="register-name" placeholder="Full name" maxLength={100}
                  className={`w-full pl-10 pr-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-primary/50 outline-none ${errors.name ? 'border-red-500/50' : 'border-white/10 focus:border-primary'}`} />
              </div>
              {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
            </div>
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="email" required value={form.email} onChange={(e) => { setForm({...form, email: e.target.value}); if (errors.email) setErrors({...errors, email: null}); }}
                  data-testid="register-email" placeholder="Email" maxLength={254}
                  className={`w-full pl-10 pr-4 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-primary/50 outline-none ${errors.email ? 'border-red-500/50' : 'border-white/10 focus:border-primary'}`} />
              </div>
              {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
            </div>
            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type={showPass ? 'text' : 'password'} required minLength={6} value={form.password} onChange={(e) => { setForm({...form, password: e.target.value}); if (errors.password) setErrors({...errors, password: null}); }}
                  data-testid="register-password" placeholder="Password (min 6 chars)"
                  className={`w-full pl-10 pr-10 h-12 bg-white/5 border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-primary/50 outline-none ${errors.password ? 'border-red-500/50' : 'border-white/10 focus:border-primary'}`} />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="register-toggle-password">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black">
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </section>
    </>
  );
}
